import {
	memo,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type RefObject,
} from "react";
import { defaultRangeExtractor, useVirtualizer, type Range } from "@tanstack/react-virtual";
import { useChatStore, selectIsCompacting } from "@/stores/useChatStore";
import { Icon } from "@/components/common/Icon";
import {
	deriveStreamingView,
	groupBlocks,
	hasActiveSubagentRun,
	isLastInTurn,
	projectMessages,
	type ViewBlock,
} from "@/lib/chat-render";
import { ChatMessage } from "./ChatMessage";
import { ChatSkeleton } from "./ChatSkeleton";
import { StreamingAssistantMessage } from "./StreamingAssistantMessage";
import { ToolGroupCard } from "./ToolGroupCard";
import { ThinkingBlock } from "./ThinkingBlock";
import { CompactionDivider } from "./CompactionDivider";
import { SystemNoticeCard } from "./SystemNoticeCard";
import { ProviderOutageCard } from "./ProviderOutageCard";
import { NewChatEnvironmentStatus } from "./NewChatEnvironmentStatus";
import { QueuedMessages } from "./QueuedMessages";
import { useChatRunsSync } from "./useChatRunsSync";
import { shouldAdjustScrollOnResize, stableBlockId } from "./chat-scroll-anchor";
import { useTranscriptSearch } from "./useTranscriptSearch";

/** 未計測のブロックの仮の高さ (px)。 */
const ESTIMATED_BLOCK_HEIGHT = 120;

/** 画面外に余分に描いておくブロック数。速いスクロールで空白が見えないようにする。 */
const OVERSCAN_BLOCKS = 6;

/** スクロール領域の内容の先頭から element までの距離。 */
function offsetWithinScroll(element: HTMLElement, scrollElement: HTMLElement): number {
	return (
		element.getBoundingClientRect().top -
		scrollElement.getBoundingClientRect().top +
		scrollElement.scrollTop
	);
}

interface ChatViewProps {
	/** 履歴をスクロールさせる要素。画面内のブロックだけを描くため、その位置と大きさを見る。 */
	scrollRef: RefObject<HTMLDivElement | null>;
}

export function ChatView({ scrollRef }: ChatViewProps) {
	const messages = useChatStore((s) => s.messages);
	const chatId = useChatStore((s) => s.chatId);
	const isDraft = chatId === null;
	const queuedCount = useChatStore((s) => s.queuedMessages.length);
	const pendingUserMessageId = useChatStore((s) => s.pendingUserMessageId);
	const waitingToolUseId = useChatStore((s) => s.hitlState?.toolUseId ?? null);
	const status = useChatStore((s) => s.status);
	const streamingAssistant = useChatStore((s) => s.streamingAssistant);
	const isCompacting = useChatStore(selectIsCompacting);
	const isLoadingChat = useChatStore((s) => s.isLoadingChat);
	const chatArchived = useChatStore((s) => s.chatArchived);
	const subagentRuns = useChatStore((s) => s.subagentRuns);
	const setSubagentRuns = useChatStore((s) => s.setSubagentRuns);
	const workflowRuns = useChatStore((s) => s.workflowRuns);
	const setWorkflowRuns = useChatStore((s) => s.setWorkflowRuns);
	const subagentRunMap = useMemo(
		() => new Map((subagentRuns ?? []).map((run) => [run.id, run])),
		[subagentRuns],
	);
	const subagentRunIdByToolUseId = useMemo(
		() => new Map((subagentRuns ?? []).map((run) => [run.toolUseId, run.id])),
		[subagentRuns],
	);
	// workflow 行は起動した run_workflow の tool_use id で引く。
	const workflowRunByToolUseId = useMemo(
		() => new Map(workflowRuns.map((run) => [run.toolUseId, run])),
		[workflowRuns],
	);

	const blocks = useMemo(
		() =>
			groupBlocks(
				projectMessages(messages, {
					pendingUserMessageId,
					waitingToolUseId,
					subagentRuns: subagentRunMap,
					subagentRunIdByToolUseId,
					workflowRuns: workflowRunByToolUseId,
				}),
			),
		[
			messages,
			pendingUserMessageId,
			waitingToolUseId,
			subagentRunMap,
			subagentRunIdByToolUseId,
			workflowRunByToolUseId,
		],
	);
	// 一覧取得の対象となるツール呼び出しを抽出する。workflow の子 run も同じ subagent_runs に
	// 載るため、run_workflow も対象に含めないとタスク一覧が空のままになる。
	const runTools = useMemo(
		() =>
			blocks
				.flatMap((block) => (block.kind === "tool_group" ? block.tools : []))
				.filter((tool) => tool.name === "run_subagent" || tool.name === "run_workflow"),
		[blocks],
	);
	// 終端していない run は状態と経過時間を追い続ける必要がある。
	const hasActiveRun = hasActiveSubagentRun(runTools, subagentRunMap);
	// foreground は tool_use の永続化直後、run 行の作成前に一覧取得が走ることがある。
	// 成功済みまたは実行中なのに run id が未解決の間だけ再取得して、この競合から回復する。
	// run_workflow は自分自身のサブエージェント run 行を持たない (進捗は workflow 行から引く) ため、
	// subagentRunId は構造上ずっと未解決のまま。含めると未解決が永久に真になり、終わったチャット
	// でもポーリングが止まらない。
	const hasUnresolvedSubagentRun = runTools.some(
		(tool) =>
			tool.name === "run_subagent" &&
			tool.subagentRunId === undefined &&
			(tool.status === "running" || tool.status === "completed"),
	);
	useChatRunsSync(
		chatId,
		runTools,
		hasActiveRun || hasUnresolvedSubagentRun,
		setSubagentRuns,
		setWorkflowRuns,
	);

	const lastBlock = blocks.at(-1);
	// "completed"/"error" は終端 (結果到着済み)。実行中・ユーザー入力待ちのみを「進行中」とみなす。
	// request_user_choice グループは UserChoiceCard が静的な回答を描くだけなので除外する。
	const lastIsActiveTool =
		lastBlock?.kind === "tool_group" &&
		!lastBlock.tools.every((t) => t.name === "request_user_choice") &&
		lastBlock.tools.some((t) => t.status === "running" || t.status === "waiting_user");
	const { streamingText, showThinking } = deriveStreamingView(
		streamingAssistant,
		status,
		lastIsActiveTool,
	);

	// 辿っている一致のブロックは、画面外でも描いてハイライトとスクロールの対象にする。
	const activeSearchBlock = useTranscriptSearch(blocks, streamingText);
	const rangeExtractor = useCallback(
		(range: Range) => {
			const visible = defaultRangeExtractor(range);
			if (activeSearchBlock === null || activeSearchBlock >= range.count) return visible;
			if (visible.includes(activeSearchBlock)) return visible;
			return [...visible, activeSearchBlock].sort((a, b) => a - b);
		},
		[activeSearchBlock],
	);
	const [listElement, setListElement] = useState<HTMLDivElement | null>(null);
	const [scrollMargin, setScrollMargin] = useState(0);
	useLayoutEffect(() => {
		const scrollElement = scrollRef.current;
		if (listElement === null || scrollElement === null) return;
		setScrollMargin(offsetWithinScroll(listElement, scrollElement));
	}, [listElement, scrollRef]);

	// 長い履歴を全部描くと、画面外のブロックまで再描画・レイアウトし続けて重くなるため、画面内だけを描く。
	// anchorTo "end" は使わない。末尾判定がリスト後ろの生成中の表示などを数えず、
	// 最下部にいないのに最下部へ寄せてしまう。最下部への追従は CenterPane が持つ。
	const virtualizer = useVirtualizer({
		count: blocks.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ESTIMATED_BLOCK_HEIGHT,
		getItemKey: (index) => blocks[index].key,
		overscan: OVERSCAN_BLOCKS,
		rangeExtractor,
		scrollMargin,
	});

	// 古い履歴が先頭へ足されても、見ていたブロックを同じ位置に保つ。
	// 目印は stableBlockId で持つ。key だと、古いツールと結合したグループを見失う。
	const blocksRef = useRef(blocks);
	const committedFirstKeyRef = useRef<string | null>(null);
	const anchorRef = useRef<{ id: string; offset: number } | null>(null);
	useLayoutEffect(() => {
		virtualizer.shouldAdjustScrollPositionOnItemSizeChange = (item, _delta, instance) =>
			// 先頭へ足された直後は、下で見ていたブロックの位置へまとめて戻すので、個別にはずらさない。
			instance.options.getItemKey(0) === committedFirstKeyRef.current &&
			shouldAdjustScrollOnResize(item, instance, instance.scrollElement?.scrollTop ?? 0);
	}, [virtualizer]);
	useLayoutEffect(() => {
		blocksRef.current = blocks;
		const firstKey = blocks[0]?.key ?? null;
		const firstChanged = firstKey !== committedFirstKeyRef.current;
		committedFirstKeyRef.current = firstKey;
		const anchor = anchorRef.current;
		const scrollElement = scrollRef.current;
		if (!firstChanged || anchor === null || scrollElement === null) return;
		const index = blocks.findIndex((block) => stableBlockId(block) === anchor.id);
		if (index < 0) return;
		// getTotalSize はこのコミットで計測した高さを measurementsCache へ反映させる。
		virtualizer.getTotalSize();
		scrollElement.scrollTop = virtualizer.measurementsCache[index].start + anchor.offset;
	}, [blocks, scrollRef, virtualizer]);
	useEffect(() => {
		const scrollElement = scrollRef.current;
		if (scrollElement === null) return;
		const recordAnchor = () => {
			const item = virtualizer.getVirtualItemForOffset(scrollElement.scrollTop);
			const block = item && blocksRef.current[item.index];
			anchorRef.current =
				item && block
					? { id: stableBlockId(block), offset: scrollElement.scrollTop - item.start }
					: null;
		};
		scrollElement.addEventListener("scroll", recordAnchor, { passive: true });
		return () => scrollElement.removeEventListener("scroll", recordAnchor);
	}, [scrollRef, virtualizer]);

	if (isLoadingChat) {
		return <ChatSkeleton />;
	}

	return (
		// data-transcript: 履歴検索の対象範囲。下段ペインの実行履歴を巻き込まないための目印。
		<div data-testid="chat-view" data-transcript className="flex flex-1 flex-col">
			<div
				ref={setListElement}
				style={{
					position: "relative",
					height: virtualizer.getTotalSize(),
					// 位置の補正は virtualizer と上のエフェクトが行う。ブラウザの補正と二重にずれないよう外す。
					overflowAnchor: "none",
				}}
			>
				{virtualizer.getVirtualItems().map((item) => (
					<div
						key={item.key}
						ref={virtualizer.measureElement}
						data-index={item.index}
						data-search-index={item.index}
						style={{
							position: "absolute",
							top: item.start - scrollMargin,
							left: 0,
							width: "100%",
						}}
					>
						<ChatBlock
							block={blocks[item.index]}
							next={blocks[item.index + 1]}
							canRetry={item.index === blocks.length - 1 && status === "idle" && !chatArchived}
						/>
					</div>
				))}
			</div>
			{blocks.length === 0 && (
				<div className="grid flex-1 grid-rows-2 justify-items-center text-fg-3">
					<div className="flex flex-col items-center gap-1 self-end">
						<Icon name="chat" size={40} stroke={1} />
						<p className="text-ui font-medium text-fg-1">エージェントとの会話をしましょう！</p>
						<p className="text-ui mt-1">このエージェントの環境の上でチャットを進められます。</p>
					</div>
					{isDraft && (
						<div className="self-start mt-15">
							<NewChatEnvironmentStatus />
						</div>
					)}
				</div>
			)}
			{streamingText.length > 0 && (
				<StreamingAssistantMessage text={streamingText} searchIndex={blocks.length} />
			)}
			{showThinking && (
				<ThinkingBlock
					live
					compacting={isCompacting}
					advising={streamingAssistant?.phase === "advising"}
					preparingTool={streamingAssistant?.phase === "preparingTool"}
					toolName={streamingAssistant?.toolName}
				/>
			)}
			{queuedCount > 0 && <QueuedMessages />}
		</div>
	);
}

interface ChatBlockProps {
	block: ViewBlock;
	next: ViewBlock | undefined;
	canRetry: boolean;
}

/** スクロールのたびに一覧ごと描き直されるため、ブロック自体が変わらない限り描き直さない。 */
const ChatBlock = memo(function ChatBlock({ block, next, canRetry }: ChatBlockProps) {
	switch (block.kind) {
		case "tool_group":
			return <ToolGroupCard tools={block.tools} />;
		case "compaction":
			return <CompactionDivider summaryText={block.summaryText} blockKey={block.key} />;
		case "system_notice":
			return <SystemNoticeCard noticeKind={block.noticeKind} text={block.text} />;
		case "provider_outage":
			return <ProviderOutageCard block={block} canRetry={canRetry} />;
		case "text":
			return <ChatMessage message={block} isLastInTurn={isLastInTurn(block, next)} />;
	}
});
